import { useEffect, useState } from "react";
import { Link, useLocation, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { bk, errMsg } from "@/lib/booking";
import { useSeo, businessLd } from "@/lib/seo";
import { CtaButtons } from "@/components/site/SiteLayout";
import landing from "@/data/landing.json";

const Page = ({ h1, children }) => (
  <main className="mx-auto max-w-3xl px-5 py-12">
    <h1 className="font-heading text-4xl font-extrabold text-stone-900">{h1}</h1>
    <div className="mt-6 space-y-4 text-stone-700 leading-relaxed [&_h2]:font-heading [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-stone-900 [&_h2]:pt-4">{children}</div>
  </main>
);

const FAQ = [
  ["Hvordan beregnes prisen?", "Prisen er den høyeste av kvadratmeterprisen og minsteprisen for tjenesten, pluss tillegg du velger. Du ser totalprisen før du bestiller."],
  ["Hva er forskjellen på engangsrenhold og fast avtale?", "Engangsrenhold er én jobb du bestiller og betaler for direkte. Fast avtale er en forespørsel om gjentakende renhold – vi gir en veiledende pris og avtaler detaljene før noe blir bindende."],
  ["Tar dere med rengjøringsprodukter?", "Ja, hvis du ønsker det. Du kan også be oss bruke produktene du har hjemme."],
  ["Bruker dere aldri rengjøringsmidler?", "Jo. Vi bruker vann og mikrofiber der det er tilstrekkelig, men riktige produkter der hygiene, smuss eller overflaten krever det."],
  ["Kan jeg avbestille?", "Ja, kostnadsfritt frem til 24 timer før oppdraget via Min side. Senere avbestilling må avtales med oss."],
  ["Må jeg være hjemme?", "Nei. Skriv hvordan vi kommer inn i instruksjonsfeltet når du bestiller."],
  ["Hvordan betaler jeg?", "Med kort på nett eller faktura etter utført jobb, avhengig av hva som er tilgjengelig når du bestiller."],
  ["Er prisene inkludert mva?", "Prisen du ser i bestillingen er totalprisen. Eventuell mva vises tydelig i prisoversikten."],
];

export const Faq = () => {
  useSeo({ title: "Ofte stilte spørsmål om renhold | Ask-Stokke Renhold", description: "Svar på vanlige spørsmål om pris, bestilling, avbestilling, produkter og faste renholdsavtaler hos Ask-Stokke Renhold.",
    jsonLd: { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) } });
  return <Page h1="Ofte stilte spørsmål">{FAQ.map(([q, a]) => <div key={q}><h2>{q}</h2><p>{a}</p></div>)}</Page>;
};

export const About = () => {
  useSeo({ title: "Om Ask-Stokke Renhold", description: "Ask-Stokke Renhold er et lokalt renholdsfirma i Asker-området med fokus på tydelige priser, gode rutiner og bevisste produktvalg." });
  return (
    <Page h1="Om Ask-Stokke Renhold">
      <p>Ask-Stokke Renhold er et lokalt renholdsfirma med utgangspunkt i Asker. Vi vil gjøre det enkelt å få hjelp til renhold – med tydelige priser, ekte ledige tider og et resultat du blir fornøyd med.</p>
      <h2>Vi lytter til deg</h2>
      <p>Alle hjem er forskjellige. Du kan alltid fortelle oss hva som er viktigst, hvilke produkter du foretrekker, og om det er allergier, barn eller kjæledyr vi skal ta hensyn til.</p>
      <h2>Bevisste valg</h2>
      <p>Vi prioriterer vann, mikrofiber og enkle metoder der det fungerer, og bruker riktige produkter når hygiene eller overflaten krever det.</p>
      <div className="pt-4"><CtaButtons /></div>
    </Page>
  );
};

export const Health = () => {
  useSeo({ title: "Renhold og helse – bevisste produktvalg | Ask-Stokke Renhold", description: "Hvorfor vi prioriterer vann og mikrofiber der det passer, og hva forskning fra Universitetet i Bergen har undersøkt om rengjøring og lungefunksjon." });
  return (
    <Page h1="Renhold og helse">
      <p>Forskere ved Universitetet i Bergen har fulgt over 6 000 personer i omtrent 20 år og undersøkt sammenhenger mellom rengjøring, bruk av rengjøringsprodukter og lungefunksjon. Studien fant at lungefunksjonen falt raskere blant kvinner som rengjorde regelmessig – hjemme eller i jobb – enn blant kvinner som ikke gjorde det. Forskerne pekte særlig på sprayprodukter og sterke midler som en mulig årsak.</p>
      <p><a className="underline text-[#2F5D50]" href="https://www.uib.no/en/cih/127475/cleaning-can-be-bad-your-health" target="_blank" rel="noreferrer">Les omtalen av studien hos Universitetet i Bergen</a>.</p>
      <h2>Hva betyr det for oss?</h2>
      <p>Studien viser en sammenheng, ikke at alle rengjøringsprodukter er farlige. Vi tar likevel funnene på alvor ved å velge metoder med omtanke: vann og mikrofiber der det holder, mildere produkter der de passer, og ingen unødvendig spraying.</p>
      <h2>Hygiene går foran</h2>
      <p>På bad, toalett og kjøkken kan det være nødvendig med egnede rengjøringsmidler. Vi blander aldri produkter og følger alltid bruksanvisningen. Eddik og grønnsåpe passer heller ikke på alle overflater – naturstein og enkelte gulv kan for eksempel ta skade.</p>
      <h2>Du bestemmer</h2>
      <p>Når du bestiller, kan du velge om vi tar med produkter eller bruker dine, og oppgi allergier eller andre hensyn. Vi lover ikke et «giftfritt» eller «allergifritt» hjem – men vi lover å lytte og velge med omtanke.</p>
    </Page>
  );
};

export const Privacy = () => {
  useSeo({ title: "Personvernerklæring | Ask-Stokke Renhold", description: "Hvordan Ask-Stokke Renhold behandler personopplysninger ved bestilling, kontakt og bruk av nettsiden." });
  return (
    <Page h1="Personvernerklæring">
      <p>Ask-Stokke Renhold er behandlingsansvarlig for personopplysninger som samles inn via denne nettsiden. Kontakt: askstokke.renhold@gmail.com, tlf. 485 06 770.</p>
      <h2>Hva vi samler inn</h2>
      <p>Navn, e-post, telefon, adresse, informasjon om boligen og ønsker du oppgir når du bestiller, ber om fast avtale eller kontakter oss. Betalingskort håndteres av Stripe – vi lagrer aldri kortopplysninger.</p>
      <h2>Formål og grunnlag</h2>
      <p>Opplysningene brukes for å levere tjenesten, sende bekreftelser og kontakte deg om oppdraget (avtale, GDPR art. 6 nr. 1 b), og for regnskap (rettslig plikt, art. 6 nr. 1 c).</p>
      <h2>Analyse og informasjonskapsler</h2>
      <p>Google Analytics brukes bare dersom du har samtykket i banneret. Du kan trekke samtykket ved å slette nettstedsdata i nettleseren.</p>
      <h2>Lagring og deling</h2>
      <p>Opplysninger lagres så lenge det er nødvendig for oppdraget og etter regnskapsregler. Vi deler kun med leverandører som trengs for å levere tjenesten (hosting, betaling, e-post).</p>
      <h2>Dine rettigheter</h2>
      <p>Du kan be om innsyn, retting eller sletting, og klage til Datatilsynet. Ta kontakt på e-post.</p>
    </Page>
  );
};

export const Terms = () => {
  useSeo({ title: "Vilkår for bestilling | Ask-Stokke Renhold", description: "Vilkår for engangsbestillinger og faste renholdsavtaler hos Ask-Stokke Renhold: pris, betaling, avbestilling og reklamasjon." });
  return (
    <Page h1="Vilkår">
      <h2>Engangsbestilling</h2>
      <p>Prisen som vises før bestilling er totalprisen for oppdraget med valgte tillegg. Ved kortbetaling er bestillingen bekreftet når betalingen er gjennomført. Ved faktura sendes faktura etter utført jobb.</p>
      <h2>Avbestilling</h2>
      <p>Avbestilling er kostnadsfri frem til 24 timer før avtalt tid. Senere avbestilling må avtales med oss.</p>
      <h2>Fast renholdsavtale</h2>
      <p>En forespørsel om fast avtale er ikke bindende. Avtalen gjelder først når pris, frekvens og oppstart er bekreftet skriftlig av begge parter. Gjentakende betaling trekkes ikke automatisk uten egen avtale.</p>
      <h2>Kundens ansvar</h2>
      <p>Kunden sørger for adkomst, strøm og vann, og gir beskjed om skjøre eller spesielle overflater. Ved flyttevask skal boligen være tømt.</p>
      <h2>Reklamasjon</h2>
      <p>Er du ikke fornøyd, gi beskjed så raskt som mulig og helst innen 48 timer, så finner vi en løsning.</p>
    </Page>
  );
};

export const Areas = () => {
  useSeo({ title: "Områder vi dekker – Asker, Bærum, Oslo og Drammen | Ask-Stokke Renhold", description: "Ask-Stokke Renhold tar renholdsoppdrag i Asker, Bærum, Sandvika, Heggedal, Vollen, Slemmestad, Fornebu, Oslo og Drammen med omegn." });
  return (
    <Page h1="Områder vi dekker">
      <p>Vi tar oppdrag i Asker, Bærum, Sandvika, Heggedal, Vollen, Slemmestad, Fornebu, Oslo og Drammen med omegn. I bestillingen sjekker vi postnummeret ditt med en gang.</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {landing.map((l) => <Link key={l.slug} to={`/${l.slug}`} data-testid={`area-link-${l.slug}`} className="rounded-xl border border-stone-200 bg-white px-4 py-3 font-semibold hover:border-[#2F5D50]">{l.h1}</Link>)}
      </div>
    </Page>
  );
};

export const Contact = () => {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [done, setDone] = useState(null);
  useSeo({ title: "Kontakt Ask-Stokke Renhold", description: "Kontakt Ask-Stokke Renhold på telefon 485 06 770 eller e-post askstokke.renhold@gmail.com, eller send oss en melding." });
  const submit = async (e) => {
    e.preventDefault();
    try { setDone(await bk.contact(f)); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Page h1="Kontakt oss">
      <p>Ring <a href="tel:+4748506770" className="font-semibold text-[#2F5D50]">485 06 770</a> eller send e-post til <a href="mailto:askstokke.renhold@gmail.com" className="font-semibold text-[#2F5D50]">askstokke.renhold@gmail.com</a>.</p>
      {done ? (
        <p data-testid="contact-success" className="rounded-xl bg-[#E7EFE9] p-4">Takk! Meldingen er mottatt.{done.email_sent ? " Du har fått en kopi på e-post." : ""}</p>
      ) : (
        <form onSubmit={submit} className="grid gap-3" data-testid="contact-form">
          <div className="grid gap-3 sm:grid-cols-3">
            <Input aria-label="Navn" data-testid="contact-name" required placeholder="Navn" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
            <Input aria-label="E-post" data-testid="contact-email" required type="email" placeholder="E-post" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
            <Input aria-label="Telefon" data-testid="contact-phone" placeholder="Telefon" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
          </div>
          <Textarea aria-label="Melding" data-testid="contact-message" required minLength={5} placeholder="Hva kan vi hjelpe med?" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} />
          <button data-testid="contact-submit" className="w-fit rounded-full bg-[#2F5D50] px-6 py-2.5 font-bold text-white">Send melding</button>
        </form>
      )}
    </Page>
  );
};

export const Landing = () => {
  const { pathname } = useLocation();
  const cfg = useOutletContext();
  const p = landing.find((l) => `/${l.slug}` === pathname);
  const [cat, setCat] = useState(null);
  useEffect(() => { bk.catalog().then(setCat); }, []);
  const svc = cat?.services.find((s) => s.slug === p.service);
  useSeo({ title: p.title, description: p.desc, path: pathname,
    jsonLd: cfg ? [businessLd(cfg, { areaServed: p.place }), { "@context": "https://schema.org", "@type": "Service", name: p.h1, serviceType: svc?.name || p.h1, areaServed: p.place, provider: { "@type": "LocalBusiness", name: "Ask-Stokke Renhold" } },
      { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [{ "@type": "ListItem", position: 1, name: "Forside", item: window.location.origin + "/" }, { "@type": "ListItem", position: 2, name: p.h1 }] }] : null });
  const related = landing.filter((l) => l.slug !== p.slug && (l.place === p.place || l.service === p.service)).slice(0, 4);
  const recurringOnly = svc && !svc.bookable;
  return (
    <main className="mx-auto max-w-3xl px-5 py-12" data-testid="landing-page">
      <nav className="text-xs text-stone-500" aria-label="Brødsmuler"><Link to="/">Forside</Link> / <Link to="/omrader">Områder</Link> / {p.h1}</nav>
      <h1 className="mt-3 font-heading text-4xl font-extrabold text-stone-900">{p.h1}</h1>
      <p className="mt-4 text-lg text-stone-700">{p.intro}</p>
      {svc && (
        <div className="mt-6 rounded-2xl border border-stone-200 bg-white p-5">
          <h2 className="font-heading font-bold">{svc.name} – pris</h2>
          <p className="mt-1 text-[#2F5D50] font-semibold">{svc.bookable ? (svc.unit === "vindu" ? `Fra ${svc.unit_price} kr per vindu` : `${svc.price_sqm} kr per m², minstepris ${svc.min_price} kr`) : "Veiledende pris i forespørselen, endelig pris etter vurdering"}</p>
          <Link to={recurringOnly ? "/fast-renholdsavtale" : `/bestill?tjeneste=${svc.slug}`} data-testid="landing-cta" className="mt-4 inline-block rounded-full bg-[#2F5D50] px-6 py-3 font-bold text-white">
            {recurringOnly ? "Be om fast avtale" : `Bestill ${svc.name.toLowerCase()}`}
          </Link>
        </div>
      )}
      <div className="mt-8 space-y-4 text-stone-700 leading-relaxed">{p.body.map((t) => <p key={t.slice(0, 20)}>{t}</p>)}</div>
      <h2 className="mt-10 font-heading text-2xl font-extrabold">Spørsmål og svar</h2>
      {p.faq.map(([q, a]) => <div key={q} className="mt-4"><h3 className="font-bold text-stone-900">{q}</h3><p className="text-stone-700">{a}</p></div>)}
      <div className="mt-10"><CtaButtons /></div>
      {related.length > 0 && (
        <div className="mt-10">
          <h2 className="font-heading font-bold">Se også</h2>
          <div className="mt-2 flex flex-wrap gap-2">{related.map((l) => <Link key={l.slug} to={`/${l.slug}`} className="rounded-full border border-stone-200 bg-white px-3 py-1 text-sm hover:border-[#2F5D50]">{l.h1}</Link>)}</div>
        </div>
      )}
    </main>
  );
};
